const MOBILE_QUERY = "(max-width: 991px)";
const SUBMENU_TRANSITION_MS = 500;
const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
].join(",");

function dm_nav() {
    const header = document.querySelector("#nav-header");
    const body = document.body;
    const toggle = header?.querySelector(".nav-toggle");
    const panel = header?.querySelector("#primary-navigation");

    if (!header || !toggle || !panel) return;

    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const disclosures = Array.from(panel.querySelectorAll(".nav-disclosure"));
    const submenus = Array.from(panel.querySelectorAll(".sub-menu"));
    const topLevelControls = Array.from(panel.querySelectorAll(
        ":scope > ul > li > a, :scope > ul > li > .nav-disclosure"
    ));
    const mobilePanelContent = Array.from(panel.children).filter(
        (element) => !element.classList.contains("header-logo")
    );
    const backgroundElements = Array.from(body.children).filter(
        (element) => element !== header && !element.hasAttribute("inert")
    );
    let outerOpen = false;
    let stackingTimers = [];
    const hideTimers = new WeakMap();

    if (window.scrollY === 0) body.classList.add("scrolled-top");

    const getSubmenu = (disclosure) => {
        const submenuId = disclosure?.getAttribute("aria-controls");
        return submenuId ? document.getElementById(submenuId) : null;
    };

    const setElementInert = (element, inert) => {
        element.inert = inert;
        if (inert) {
            element.setAttribute("inert", "");
        } else {
            element.removeAttribute("inert");
        }
    };

    const clearHideTimer = (submenu) => {
        const timer = hideTimers.get(submenu);
        if (timer) window.clearTimeout(timer);
        hideTimers.delete(submenu);
    };

    const setSubmenuState = (disclosure, expanded, immediate = false) => {
        const submenu = getSubmenu(disclosure);
        if (!submenu) return;

        clearHideTimer(submenu);
        disclosure.setAttribute("aria-expanded", expanded ? "true" : "false");
        disclosure.setAttribute(
            "aria-label",
            expanded ? disclosure.dataset.expandedLabel : disclosure.dataset.collapsedLabel
        );

        if (expanded) {
            submenu.hidden = false;
            setElementInert(submenu, false);
            void submenu.offsetWidth;
            disclosure.classList.add("is-active");
            disclosure.closest(".menu-item-has-children")?.classList.add("is-active");
            submenu.classList.add("is-active");
            return;
        }

        disclosure.classList.remove("is-active");
        disclosure.closest(".menu-item-has-children")?.classList.remove("is-active");
        submenu.classList.remove("is-active");
        setElementInert(submenu, true);

        if (immediate || reducedMotionQuery.matches) {
            submenu.hidden = true;
            submenu.classList.remove("z2");
            return;
        }

        hideTimers.set(submenu, window.setTimeout(() => {
            if (disclosure.getAttribute("aria-expanded") === "false") {
                submenu.hidden = true;
                submenu.classList.remove("z2");
            }
            hideTimers.delete(submenu);
        }, SUBMENU_TRANSITION_MS));
    };

    const setDesktopStacking = (activeSubmenu = null) => {
        stackingTimers.forEach((timer) => window.clearTimeout(timer));
        stackingTimers = [];

        if (mobileQuery.matches || reducedMotionQuery.matches) {
            submenus.forEach((submenu) => submenu.classList.remove("z2"));
            activeSubmenu?.classList.add("z2");
            return;
        }

        stackingTimers.push(window.setTimeout(() => {
            submenus.forEach((submenu) => submenu.classList.remove("z2"));
        }, 450));

        if (activeSubmenu) {
            stackingTimers.push(window.setTimeout(() => {
                if (activeSubmenu.classList.contains("is-active")) activeSubmenu.classList.add("z2");
            }, 451));
        }
    };

    const getExpandedDisclosures = () => disclosures.filter(
        (disclosure) => disclosure.getAttribute("aria-expanded") === "true"
    );

    const setTopLevelControlsInert = (inert) => {
        topLevelControls.forEach((control) => setElementInert(control, inert));
    };

    const getAncestorDisclosures = (disclosure) => {
        const ancestors = [];
        let parentSubmenu = disclosure.closest(".sub-menu");

        while (parentSubmenu) {
            const parentDisclosure = parentSubmenu.parentElement?.querySelector(":scope > .nav-disclosure");
            if (!parentDisclosure) break;
            ancestors.unshift(parentDisclosure);
            parentSubmenu = parentDisclosure.closest(".sub-menu");
        }

        return ancestors;
    };

    const closeSubmenu = (disclosure, restoreFocus = true) => {
        const submenu = getSubmenu(disclosure);
        if (!submenu) return;

        getExpandedDisclosures()
            .filter((expanded) => expanded === disclosure || submenu.contains(expanded))
            .reverse()
            .forEach((expanded) => setSubmenuState(expanded, false));

        const hasOpenSubmenu = getExpandedDisclosures().length > 0;
        panel.classList.toggle("has-open-submenu", hasOpenSubmenu);
        if (mobileQuery.matches) setTopLevelControlsInert(hasOpenSubmenu);
        if (restoreFocus) disclosure.focus();
    };

    const closeAllSubmenus = (restoreFocus = false, immediate = false) => {
        const expanded = getExpandedDisclosures();
        expanded.slice().reverse().forEach((disclosure) => setSubmenuState(disclosure, false, immediate));
        panel.classList.remove("has-open-submenu");
        setTopLevelControlsInert(false);
        setDesktopStacking();

        if (restoreFocus && expanded.length) expanded[0].focus();
    };

    const openSubmenu = (disclosure) => {
        const submenu = getSubmenu(disclosure);
        if (!submenu) return;

        panel.classList.add("has-open-submenu");
        body.classList.add("menu-open");

        const ancestors = getAncestorDisclosures(disclosure);
        getExpandedDisclosures()
            .filter((expanded) => !ancestors.includes(expanded))
            .forEach((expanded) => setSubmenuState(expanded, false));

        ancestors.forEach((ancestor) => setSubmenuState(ancestor, true));
        setSubmenuState(disclosure, true);
        setDesktopStacking(submenu);

        if (mobileQuery.matches) {
            setTopLevelControlsInert(true);
            window.requestAnimationFrame(() => submenu.querySelector(".nav-back")?.focus());
        }
    };

    const setBackgroundInert = (inert) => {
        backgroundElements.forEach((element) => setElementInert(element, inert));
    };

    const setMobilePanelContentInert = (inert) => {
        mobilePanelContent.forEach((element) => setElementInert(element, inert));
    };

    const openOuterPanel = () => {
        if (!mobileQuery.matches || outerOpen) return;

        outerOpen = true;
        toggle.classList.add("is-active");
        toggle.setAttribute("aria-expanded", "true");
        toggle.setAttribute("aria-label", toggle.dataset.closeLabel);
        setMobilePanelContentInert(false);
        setBackgroundInert(true);
        body.classList.add("menu-open");

        window.requestAnimationFrame(() => panel.querySelector(FOCUSABLE_SELECTOR)?.focus());
    };

    const closeOuterPanel = (restoreFocus = true) => {
        closeAllSubmenus(false);
        outerOpen = false;
        toggle.classList.remove("is-active");
        toggle.setAttribute("aria-expanded", "false");
        toggle.setAttribute("aria-label", toggle.dataset.openLabel);
        body.classList.remove("menu-open");
        setBackgroundInert(false);

        if (mobileQuery.matches) setMobilePanelContentInert(true);
        if (restoreFocus) toggle.focus();
    };

    const resetNavigation = () => {
        closeAllSubmenus(false, true);
        outerOpen = false;
        toggle.classList.remove("is-active");
        toggle.setAttribute("aria-expanded", "false");
        toggle.setAttribute("aria-label", toggle.dataset.openLabel);
        body.classList.remove("menu-open");
        setBackgroundInert(false);
        setTopLevelControlsInert(false);
        setMobilePanelContentInert(mobileQuery.matches);
    };

    const getVisibleFocusableElements = () => Array.from(header.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.closest("[hidden], [inert]") && element.getClientRects().length > 0
    );

    toggle.addEventListener("click", () => outerOpen ? closeOuterPanel() : openOuterPanel());

    disclosures.forEach((disclosure) => {
        disclosure.addEventListener("click", () => {
            if (disclosure.getAttribute("aria-expanded") === "true") {
                closeSubmenu(disclosure);
                if (!mobileQuery.matches && getExpandedDisclosures().length === 0) {
                    panel.classList.remove("has-open-submenu");
                    body.classList.remove("menu-open");
                }
                return;
            }

            openSubmenu(disclosure);
        });
    });

    panel.addEventListener("click", (event) => {
        const back = event.target.closest(".nav-back");
        if (back) {
            const submenu = back.closest(".sub-menu");
            const disclosure = submenu?.parentElement?.querySelector(":scope > .nav-disclosure");
            if (disclosure) closeSubmenu(disclosure);
            if (getExpandedDisclosures().length === 0) panel.classList.remove("has-open-submenu");
            return;
        }

        if (!mobileQuery.matches && event.target === panel && getExpandedDisclosures().length) {
            closeAllSubmenus(true);
            body.classList.remove("menu-open");
        }
    });

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            const expanded = getExpandedDisclosures();
            if (expanded.length) {
                event.preventDefault();
                closeSubmenu(expanded[expanded.length - 1]);
                if (!mobileQuery.matches && getExpandedDisclosures().length === 0) {
                    panel.classList.remove("has-open-submenu");
                    body.classList.remove("menu-open");
                }
            } else if (mobileQuery.matches && outerOpen) {
                event.preventDefault();
                closeOuterPanel();
            }
            return;
        }

        if (event.key !== "Tab" || !mobileQuery.matches || !outerOpen) return;

        const focusable = getVisibleFocusableElements();
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    mobileQuery.addEventListener("change", resetNavigation);
    resetNavigation();

    if (!document.cookie.split("; ").find((row) => row.startsWith("animationPlayed="))) {
        body.classList.add("animate-intro");
        document.cookie = "animationPlayed=true; path=/";
    }
}

export { dm_nav };
